// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░▄▀░█▀▄░█▀▀░█▀▀░█░█░█░░░█▀█░█▀▄░░░░░█░░░█▀█░█░█░█▀█░█░█░▀█▀░▀▄░░░░░░░░
// ░░░░░░░▀▄░░█▀▄░█▀▀░█░█░█░█░█░░░█▀█░█▀▄░▀▀▀░█░░░█▀█░░█░░█░█░█░█░░█░░░▄▀░░░░░░░
// ░░░░░░░░░▀░▀░▀░▀▀▀░▀▀▀░▀▀▀░▀▀▀░▀░▀░▀░▀░░░░░▀▀▀░▀░▀░░▀░░▀▀▀░▀▀▀░░▀░░▀░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ┏━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┓
// ┃  *  Copyright (c) 2026, the Regular Layout Authors. This file is part  *  ┃
// ┃  *  of the Regular Layout library, distributed under the terms of the  *  ┃
// ┃  *  [Apache License 2.0](https://www.apache.org/licenses/LICENSE-2.0). *  ┃
// ┗━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┛

import type { OverlayMode } from "./types";

/**
 * Instance-specific constants which define the behavior and rendering details
 * of a `<regular-layout>`.
 */
export interface Physics {
	/**
	 * The prefix to use for `CustomEvent`s generated by `regular-layout`, e.g.
	 * `"regular-layout-before-update"`.
	 */
	CUSTOM_EVENT_NAME_PREFIX: string;

	/**
	 * The attribute name to use for matching child `Element`s to grid
	 * positions.
	 */
	CHILD_ATTRIBUTE_NAME: string;

	/**
	 * The minimum number of pixels the mouse must move to be considered a drag.
	 */
	MIN_DRAG_DISTANCE: number;

	/**
	 * Should floating point pixel calculations be rounded. Useful for testing.
	 */
	SHOULD_ROUND: boolean;

	/**
	 * Class name to use for child elements in overlay position (dragging).
	 */
	OVERLAY_CLASSNAME: string;

	/**
	 * The percentage of the maximum resize distance that will be clamped.
	 */
	MINIMUM_REDISTRIBUTION_SIZE_THRESHOLD: number;

	/**
	 * Threshold from panel edge that is considered a split vs drop action.
	 */
	SPLIT_EDGE_TOLERANCE: number;

	/**
	 * Threshold from _container_ edge that is considered a split action on the root
	 * node.
	 */
	SPLIT_ROOT_EDGE_TOLERANCE: number;

	/**
	 * Tolerance threshold for considering two grid track positions as identical.
	 *
	 * When collecting and deduplicating track positions, any positions closer than
	 * this value are treated as the same position to avoid redundant grid tracks.
	 */
	GRID_TRACK_COLLAPSE_TOLERANCE: number;

	/**
	 * The overlay default behavior.
	 */
	OVERLAY_DEFAULT: OverlayMode;

	/**
	 * Width of split panel dividers in pixels (for hit-test purposes).
	 */
	GRID_DIVIDER_SIZE: number;

	/**
	 * Whether the grid should trigger column resize if the grid itself is not
	 * the `event.target`.
	 */
	GRID_DIVIDER_CHECK_TARGET: boolean;
}

/**
 * Like `GlobalPhysics`, but suitable for partial definition for incremental
 * updates.
 */
export interface PhysicsUpdate {
	CUSTOM_EVENT_NAME_PREFIX?: string;
	CHILD_ATTRIBUTE_NAME?: string;
	MIN_DRAG_DISTANCE?: number;
	SHOULD_ROUND?: boolean;
	OVERLAY_CLASSNAME?: string;
	MINIMUM_REDISTRIBUTION_SIZE_THRESHOLD?: number;
	SPLIT_EDGE_TOLERANCE?: number;
	SPLIT_ROOT_EDGE_TOLERANCE?: number;
	GRID_TRACK_COLLAPSE_TOLERANCE?: number;
	OVERLAY_DEFAULT?: OverlayMode;
	GRID_DIVIDER_SIZE?: number;
	GRID_DIVIDER_CHECK_TARGET?: boolean;
}

export const DEFAULT_PHYSICS: Physics = Object.freeze({
	CUSTOM_EVENT_NAME_PREFIX: "regular-layout",
	CHILD_ATTRIBUTE_NAME: "name",
	MIN_DRAG_DISTANCE: 10,
	SHOULD_ROUND: false,
	OVERLAY_CLASSNAME: "overlay",
	MINIMUM_REDISTRIBUTION_SIZE_THRESHOLD: 0.15,
	SPLIT_EDGE_TOLERANCE: 0.33,
	SPLIT_ROOT_EDGE_TOLERANCE: 0.03,
	GRID_TRACK_COLLAPSE_TOLERANCE: 0.001,
	OVERLAY_DEFAULT: "absolute",
	GRID_DIVIDER_SIZE: 6,
	GRID_DIVIDER_CHECK_TARGET: true,
});
